import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { Textarea } from 'primeng/textarea';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Select } from 'primeng/select';
import { MultiSelect } from 'primeng/multiselect';
import { Checkbox } from 'primeng/checkbox';
import { Tooltip } from 'primeng/tooltip';
import { catchError, of, forkJoin } from 'rxjs';
import { Task } from '../../models/task.model';
import { TaskService } from '../../services/task.service';
import { ServiceGroupService } from '../../services/service-group.service';
import { ServiceGroup } from '../../models/service-group.model';
import { AuthService } from '../../services/auth.service';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-task-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    Button,
    Dialog,
    InputText,
    ConfirmDialog,
    Select,
    MultiSelect,
    Checkbox,
    Tooltip
  ],
  providers: [ConfirmationService],
  templateUrl: './task-management.component.html',
  styleUrl: './task-management.component.scss'
})
export class TaskManagementComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  tasks: Task[] = [];
  filteredTasks: Task[] = [];
  selectedTask: Task = this.getEmptyTask();
  fieldTouched: Record<string, boolean> = {};
  displayDialog = false;
  dialogMode: 'add' | 'edit' = 'add';
  loading = false;
  searchText = '';
  selectedStatusFilter: string | null = 'active'; // Default to 'active'
  selectedServiceGroupFilter: string | null = null;
  serviceGroups: ServiceGroup[] = [];

  rows = 50;
  first = 0;
  totalRecords = 0;
  rowsPerPageOptions = [10, 20, 50, 100];

  statusFilterOptions = [
    { label: 'All', value: null },
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' }
  ];

  get totalTasksCount(): number { return this.tasks.length; }
  get activeTasksCount(): number { return this.tasks.filter(t => t.isActive).length; }
  get inactiveTasksCount(): number { return this.tasks.filter(t => !t.isActive).length; }

  constructor(
    private taskService: TaskService,
    private serviceGroupService: ServiceGroupService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private authService: AuthService,
    public pageHeaderService: PageHeaderSearchActionService
  ) {}

  ngOnInit() {
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.pageHeaderService.setPlaceholder('Search by name or description...');
    this.pageHeaderService.setPrimaryButtonLabel('Add Task');
    this.pageHeaderService.setCanShowExport(true);
    this.pageHeaderService.setExportDisabled(true);
    this.searchText = this.pageHeaderService.currentSearchText;
    this.loadData();
    this.pageHeaderService.searchText.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(v => {
      this.searchText = v ?? '';
      this.applyFilters();
    });
    this.pageHeaderService.primaryActionClick.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.openAddDialog());
    this.pageHeaderService.exportClick.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.exportToExcel());
    this.authService.user$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(user => {
      this.pageHeaderService.setCanShowPrimaryAction(!!user?.isAdmin);
    });
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  loadData() {
    this.loading = true;
    forkJoin({
      tasks: this.taskService.getAllTasks().pipe(
        catchError(error => {
          console.error('Error loading tasks:', error);
          return of([] as Task[]);
        })
      ),
      serviceGroups: this.serviceGroupService.getAllServiceGroups().pipe(
        catchError(error => {
          console.error('Error loading service groups:', error);
          return of([] as ServiceGroup[]);
        })
      )
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.tasks = (data.tasks || []).map(t => ({
          ...t,
          isActive: t.isActive !== undefined ? t.isActive : true
        }));
        this.serviceGroups = (data.serviceGroups || []).filter(sg => sg.isActive);
        // Apply filters (will filter by active by default since selectedStatusFilter = 'active')
        this.applyFilters();
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading data:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load data. Please refresh the page or try again later.'
        });
        this.loading = false;
      }
    });
  }

  applyFilters() {
    let filtered = [...this.tasks];

    if (this.searchText) {
      const searchLower = this.searchText.toLowerCase();
      filtered = filtered.filter(t =>
        t.name.toLowerCase().includes(searchLower) ||
        (t.description && t.description.toLowerCase().includes(searchLower))
      );
    }

    if (this.selectedStatusFilter === 'active') {
      filtered = filtered.filter(t => t.isActive);
    } else if (this.selectedStatusFilter === 'inactive') {
      filtered = filtered.filter(t => !t.isActive);
    }

    // Apply Service Group filter - check if task has any matching service group
    if (this.selectedServiceGroupFilter) {
      filtered = filtered.filter(t => {
        if (t.serviceGroupIds && t.serviceGroupIds.length > 0) {
          return t.serviceGroupIds.includes(this.selectedServiceGroupFilter!);
        }
        // Backward compatibility: check legacy serviceGroupId
        return t.serviceGroupId === this.selectedServiceGroupFilter;
      });
    }

    this.filteredTasks = filtered;
    this.totalRecords = filtered.length;
    this.pageHeaderService.setExportDisabled(this.filteredTasks.length === 0);
  }

  onSearch() {
    this.applyFilters();
  }

  onStatusFilterChange() {
    this.applyFilters();
  }

  clearSearch() {
    this.searchText = '';
    this.pageHeaderService.setSearchText('');
    this.applyFilters();
  }

  clearFilters() {
    this.searchText = '';
    this.pageHeaderService.setSearchText('');
    this.selectedStatusFilter = 'active'; // Reset to default 'active' view
    this.selectedServiceGroupFilter = null;
    this.applyFilters();
  }

  hasActiveFilters(): boolean {
    return !!(this.searchText.trim() ||
      (this.selectedStatusFilter !== null && this.selectedStatusFilter !== 'active') ||
      this.selectedServiceGroupFilter);
  }

  hideDialog() {
    this.displayDialog = false;
    this.selectedTask = this.getEmptyTask();
    this.fieldTouched = {};
  }

  resetTaskForm() {
    this.selectedTask = this.getEmptyTask();
    this.fieldTouched = {};
  }

  exportToExcel() {
    if (this.filteredTasks.length === 0) return;
    const headers = ['Name', 'Description', 'Service Group', 'Leave/Holiday only', 'Status'];
    const rows = this.filteredTasks.map(t => [
      t.name ?? '',
      t.description ?? '',
      this.getServiceGroupNames(t),
      t.isLeaveType ? 'Yes' : 'No',
      t.isActive ? 'Active' : 'Inactive'
    ]);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Tasks');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbout], { type: 'application/octet-stream' }), `tasks_${this.formatDateForFile()}.xlsx`);
  }

  formatDateForFile(): string {
    const d = new Date();
    return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}_${String(d.getHours()).padStart(2, '0')}${String(d.getMinutes()).padStart(2, '0')}`;
  }

  openAddDialog() {
    this.selectedTask = this.getEmptyTask();
    this.fieldTouched = {};
    this.dialogMode = 'add';
    this.displayDialog = true;
  }

  openEditDialog(task: Task) {
    this.selectedTask = {
      ...task,
      // Ensure serviceGroupIds array exists
      serviceGroupIds: task.serviceGroupIds || (task.serviceGroupId ? [task.serviceGroupId] : [])
    };
    this.fieldTouched = {};
    this.dialogMode = 'edit';
    this.displayDialog = true;
  }

  saveTask() {
    this.fieldTouched['name'] = true;
    if (!this.selectedTask.name.trim()) {
      return;
    }

    if (this.dialogMode === 'add') {
      this.taskService.createTask({
        name: this.selectedTask.name,
        description: this.selectedTask.description,
        isActive: this.selectedTask.isActive,
        isLeaveType: this.selectedTask.isLeaveType ?? false,
        serviceGroupIds: this.selectedTask.serviceGroupIds || [],
        // Backward compatibility: if serviceGroupIds is empty but serviceGroupId exists, use it
        serviceGroupId: (this.selectedTask.serviceGroupIds && this.selectedTask.serviceGroupIds.length > 0) 
          ? undefined 
          : this.selectedTask.serviceGroupId
      }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (createdTask: Task) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Task created successfully'
          });
          const normalizedTask: Task = {
            ...createdTask,
            isActive: createdTask.isActive !== undefined ? createdTask.isActive : true
          };
          this.tasks = [...this.tasks, normalizedTask];
          this.applyFilters();
          this.displayDialog = false;
        },
        error: (error) => {
          console.error('Error creating task:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to create task'
          });
        }
      });
    } else {
      this.taskService.updateTask(this.selectedTask.id, this.selectedTask).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (updatedTask: Task) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Task updated successfully'
          });
          const normalizedTask: Task = {
            ...updatedTask,
            isActive: updatedTask.isActive !== undefined ? updatedTask.isActive : true
          };
          this.tasks = this.tasks.map(t => t.id === normalizedTask.id ? normalizedTask : t);
          this.applyFilters();
          this.displayDialog = false;
        },
        error: (error) => {
          console.error('Error updating task:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update task'
          });
        }
      });
    }
  }

  deleteTask(task: Task) {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete the task "${task.name}"?`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.taskService.deleteTask(task.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Task deleted successfully'
            });
            this.tasks = this.tasks.filter(t => t.id !== task.id);
            this.applyFilters();
          },
          error: (error) => {
            console.error('Error deleting task:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to delete task'
            });
          }
        });
      }
    });
  }

  toggleTaskStatus(task: Task) {
    const activating = !task.isActive;
    this.confirmationService.confirm({
      message: `Are you sure you want to ${activating ? 'activate' : 'deactivate'} "${task.name}"?`,
      header: activating ? 'Confirm Activate' : 'Confirm Deactivate',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: activating ? 'p-button-success' : 'p-button-danger',
      accept: () => {
        const updatedTask = { ...task, isActive: !task.isActive };
        this.taskService.updateTask(task.id, updatedTask).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (returnedTask: Task) => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: `Task ${updatedTask.isActive ? 'activated' : 'deactivated'} successfully`
            });
            this.tasks = this.tasks.map(t => t.id === task.id ? { ...t, ...returnedTask } : t);
            this.applyFilters();
          },
          error: (error) => {
            console.error('Error updating task status:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to update task status'
            });
          }
        });
      }
    });
  }

  getEmptyTask(): Task {
    return {
      id: '',
      name: '',
      description: '',
      isActive: true,
      isLeaveType: false,
      serviceGroupIds: [],
      serviceGroupId: undefined
    };
  }

  getServiceGroupNames(task: Task): string {
    if (task.serviceGroupNames && task.serviceGroupNames.length > 0) {
      return task.serviceGroupNames.join(', ');
    }
    if (task.serviceGroupIds && task.serviceGroupIds.length > 0) {
      const names = task.serviceGroupIds
        .map(id => {
          const sg = this.serviceGroups.find(s => s.id === id);
          return sg ? sg.name : 'Unknown';
        })
        .filter(name => name !== 'Unknown');
      return names.length > 0 ? names.join(', ') : '-';
    }
    // Backward compatibility: check legacy serviceGroupId
    if (task.serviceGroupId) {
      return this.getServiceGroupName(task.serviceGroupId);
    }
    return '-';
  }

  getServiceGroupName(serviceGroupId?: string): string {
    if (!serviceGroupId) return '-';
    const serviceGroup = this.serviceGroups.find(sg => sg.id === serviceGroupId);
    return serviceGroup ? serviceGroup.name : 'Unknown';
  }
}

